// First-run gate (ENG-3796).
//
// One question, asked once per launch: is this user onboarded? The server
// answers it — `onboarded` is true when they have EVER added a camera OR have
// explicitly finished or skipped this screen (agentConfig/activated). So there
// is deliberately no device counting, no per-install localStorage flag, and no
// second signal to reconcile here.
//
// Three states, and the middle one is the reason this is a component rather
// than a conditional at a call site:
//
//   onboarded  -> render the app. Never shown again, across reinstalls and
//                 machines, because the answer lives in the database.
//   resolving  -> render the app with NO overlay. A returning user must never
//                 see a flash of "Welcome to Iris" while the fetch is in
//                 flight; showing nothing is always right, and the overlay
//                 appears a moment later for the few users who need it.
//   not        -> render the app with the welcome modal over it.
//
// A failed fetch is treated as "resolving forever": the app renders, the modal
// never appears. Blocking a working app behind a telemetry-ish read would be a
// worse failure than skipping the screen for one launch.
//
// Per rules: code-comments-required, reference-rules-in-comments
// (ai.knowledge/global).

// The app's OWN wrapper, never @auth0/auth0-react directly. On Electron the
// raw SDK reports isAuthenticated=false and hands back a token getter that
// cannot work, because desktop authenticates through the sidecar rather than a
// browser session; the wrapper swaps both for the Electron equivalents
// (auth/use-auth0.ts). Importing the raw hook here made the gate return early
// on every desktop launch, so the welcome screen never appeared.
import { useAuth0 } from '#~/auth/index.js';
import { useDepot } from '#~/depot/hooks.js';
import {
  type ReactElement,
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import { useLocation } from 'react-router-dom';
import { limitCanAddCamera, useBillingLimitsState } from '#~/api/billing/limits.js';
import { fetchActivation } from '#~/api/device/activated.js';
import { markOnboarded } from '#~/api/device/mark-onboarded.js';
import { useConfig } from '#~/config/hooks.js';
import { isElectronApp } from '#~/electron/utils.js';
import { ScreenPermissions } from './wizard/screen-permissions.js';
import { useOnboardingPermissionsClaim } from './store.js';
import { onboardingContextFor, useOnboardingTracker } from './hooks/use-onboarding-tracker.js';
import { PostHogFeatureFlag } from '#~/posthog/constants.js';
import { useFeatureFlag } from '#~/posthog/hooks/use-feature-flag.js';
import { BridgeFailureState } from './components/bridge-failure.js';
import { AutoLinkFailureState } from './components/auto-link-failure.js';
import { useBridgeSteps } from './hooks/use-bridge-steps.js';
import { useBridgeTimeout } from './hooks/use-bridge-timeout.js';
import { ScreenChooseMethod } from './wizard/screen-choose-method.js';
import type { Device as DeviceModel } from '@studio/models/device';
import { toDeviceId } from '@studio/models/device';
import { DEFAULT_CAMERA_CONFIG } from './wizard/types.js';
import { ScreenMagicMoment } from './wizard/screen-magic-moment.js';
import { ScreenComplete } from './wizard/screen-complete.js';
import { ScreenConnectionTest } from './wizard/screen-connection-test.js';
// ENG-3796: the camera steps are the REAL Add Video Source modal now, not
// screens of this funnel's own. See components/onboarding-add-source.tsx.
import { OnboardingAddSource } from './components/onboarding-add-source.js';
import { toConnectionTestInput, type ConnectionTestInput } from './add-handoff.js';
import type { OnboardingExit } from '#~/video/components/add/store/types.js';
import { MIN_DETECTING_DWELL_MS } from './constants.js';
import { useElapsedSince } from './hooks/use-elapsed-since.js';
import {
  bootstrapOwnsLinkingScreen,
  isAutoLinkFailedForOnboarding,
  isStudioLinkedForOnboarding,
  mayOpenFunnel,
  mayLeaveDetecting,
  cameraToResumeOn,
  decideResume,
  shouldCloseShownCamera,
  ONBOARDED_RECHECK_INTERVAL_MS,
  shouldRecheckOnboarded,
  stepAfterDetecting,
  onboardingAbandonProperties,
  selectFunnelScreen,
  screenWhileResumePending,
  mayPaintResume,
  isFunnelEnabled,
  isFunnelSupportedSurface,
  funnelDefersToRoute,
  shouldArmBridgeCeiling,
  shouldMarkOnboarded,
  type FunnelStep,
  type OnboardedState
} from './gate-decisions.js';
import { FunnelBridgeConcept } from './components/funnel-bridge-concept.js';
import { FunnelDetecting } from './components/funnel-detecting.js';
import { FunnelWelcome } from './components/funnel-welcome.js';
import { useOnboardingRestart } from './store.js';
import {
  clearOnboardingResume,
  readOnboardingResume,
  RESUME_TTL_MS,
  type OnboardingResume
} from './resume.js';
import { useLinkedRoom } from '#~/room/hook_last_id.js';
import { getAutoLinkController } from '#~/routes/verify-auto-link/v2/auto-link-controller.js';
import { bindOnboardingAutoLinkOpen } from '#~/test-probes/onboarding-auto-link-probe.js';
import { bindOnboardingOneClickControl } from '#~/test-probes/onboarding-one-click-probe.js';
import { bindOnboardingMagicMomentOpen } from '#~/test-probes/onboarding-magic-moment-probe.js';

interface OnboardingGateProps {
  readonly children: ReactNode;
}

/** Enough of the camera screen to paint before the room reports the device. */
const connectionFromResume = (record: OnboardingResume): ConnectionTestInput => ({
  name: record.name ?? '',
  host: record.host ?? '',
  video: undefined,
  control: undefined,
  modelId: undefined,
  agentId: undefined,
  addressSource: undefined,
  detected: undefined,
  committedDeviceId: record.agentId as DeviceModel['id']
});

export const OnboardingGate = ({ children }: OnboardingGateProps): ReactElement => {
  const { getAccessTokenSilently, isAuthenticated, user: auth0User } = useAuth0();
  const location = useLocation();
  const config = useConfig();
  const autoLinkState = useDepot((depot) => depot.boot.autoLinkState);
  // One owner publishes visibility; the funnel must not second-guess its latch.
  const isOverlayVisible = useDepot((depot) => depot.boot.isOverlayVisible);
  const isRootReady = useDepot((depot) => depot.boot.isRootReady);
  const hasRegistryLoaded = useDepot((depot) => depot.synchronizer.hasRegistryLoaded);
  const [state, setState] = useState<OnboardedState>('resolving');
  // ENG-3796 rollout switch. Fails closed: absent, false, unreachable, or still
  // resolving all mean "behave as before". See isFunnelEnabled.
  const funnelFlag = useFeatureFlag(PostHogFeatureFlag.ONBOARDING_FUNNEL, {
    defaultValue: false
  });
  // Test probes can open a screen only when Electron launched with IRIS_E2E_TEST.
  const [e2eOpen, setE2eOpen] = useState(false);
  // The surface check comes FIRST and outranks the E2E probe, because it is not
  // a preference — the funnel cannot work where there is no Bridge. It gates the
  // activation read and the markOnboarded write below as well as the render, so
  // a browser session never asks the server an onboarding question it will not
  // act on. See isFunnelSupportedSurface.
  const funnelEnabled =
    isFunnelSupportedSurface({ isElectron: isElectronApp() }) &&
    (e2eOpen || isFunnelEnabled(funnelFlag));
  const tracker = useOnboardingTracker();
  const [step, setStep] = useState<FunnelStep>('welcome');

  // Claim the permissions ask for as long as the funnel is showing, so the
  // standalone OnboardPermissionsModal on the room route does not open a second
  // dialog underneath this one (ENG-3796). Released on unmount and whenever the
  // funnel stops rendering, so a user who skips or dismisses still gets asked.
  const claimPermissions = useOnboardingPermissionsClaim((µ) => µ.claimPermissions);
  const releasePermissions = useOnboardingPermissionsClaim((µ) => µ.releasePermissions);

  // Everything the connection test needs, resolved by the Add Video Source
  // workflow and handed over at its last shared step.
  //
  // ONE piece of state where there used to be eight. The funnel used to carry
  // the camera, its control config, its model, which fork produced it, the
  // Bridge that found it, the discovery's video object, that object's
  // provenance and an ONVIF endpoint — each set from a different screen, and
  // several of them reconciled against each other on the way to the connection
  // test. The shared panels resolve all of it now (the ONVIF stream URI
  // included, see add-handoff.ts), so the funnel holds the answer rather than
  // the ingredients.
  // A run interrupted by the reload that linking an embedded camera forces.
  //
  // Read ONCE, synchronously, before the first paint. The note survives the
  // reload and already names the camera, so the magic moment paints on that
  // first paint instead of waiting for the room to report the device.
  // See resume.ts and screenWhileResumePending.
  const [resumeRecord] = useState(() => readOnboardingResume());
  const [resumePending, setResumePending] = useState(() => resumeRecord !== undefined);
  const [connection, setConnection] = useState<ConnectionTestInput | undefined>(() =>
    resumeRecord === undefined ? undefined : connectionFromResume(resumeRecord)
  );
  // The device the connection test added and kept. Until the room reports the
  // real id, the saved agent id keeps the camera screen mounted. The viewer
  // shows "Connecting to camera…" until that id is replaced.
  const [addedDeviceId, setAddedDeviceId] = useState<DeviceModel['id'] | undefined>(() =>
    resumeRecord === undefined ? undefined : (resumeRecord.agentId as DeviceModel['id'])
  );
  const resumedRef = useRef(false);
  // An older camera must never count as the linked camera arriving or leaving.
  const sawResumeCameraRef = useRef(false);

  const dropResume = useCallback(() => {
    clearOnboardingResume();
    setResumePending(false);
    resumedRef.current = true;
  }, []);

  useEffect(() => {
    if (resumeRecord === undefined) return;
    const remaining = resumeRecord.startedAt + RESUME_TTL_MS - Date.now();
    if (remaining <= 0) {
      dropResume();
      return;
    }
    const timer = window.setTimeout(() => {
      dropResume();
    }, remaining);
    return () => window.clearTimeout(timer);
  }, [resumeRecord, dropResume]);
  const { roomId: linkedRoomId } = useLinkedRoom();

  // The room's cameras, as the depot reports them. Only consulted while
  // resuming; the funnel has no other reason to look.
  //
  // The SELECTOR returns the devices object the store already holds, and the
  // list is derived from it. A selector that built the array itself would hand
  // zustand a new reference on every read and spin the component in a
  // re-render loop.
  const roomDevices = useDepot(
    (state: {
      rooms: Record<string, { devices?: Record<string, unknown> } | undefined>;
    }): Record<string, unknown> | undefined =>
      linkedRoomId === undefined ? undefined : state.rooms[linkedRoomId]?.devices
  );

  const roomCameras = useMemo(
    () =>
      Object.entries(roomDevices ?? {}).flatMap(([id, device]) => {
        const typed = device as {
          name?: unknown;
          video?: { config?: { host?: unknown } } | undefined;
        };
        if (typed.video === undefined) return [];

        const host = typed.video.config?.host;

        return [
          {
            id,
            name: typeof typed.name === 'string' ? typed.name : 'Camera',
            host: typeof host === 'string' ? host : ''
          }
        ];
      }),
    [roomDevices]
  );

  // Bumped by Try Again on the failure screen: restarts the connecting wait.
  const [retryCount, setRetryCount] = useState(0);
  // Sticky for the launch: how this funnel was entered. See FUNNEL_ENTERED.
  const restartedRef = useRef(false);

  useEffect(() => {
    // The discovery probe injected a fixture list into the funnel's own
    // discovery screen, and that screen is the production Add Video Source
    // modal now — there is no funnel-local list left for a fixture to replace.
    const unbindAutoLink = bindOnboardingAutoLinkOpen(() => {
      setE2eOpen(true);
      setState('not-onboarded');
      setStep('welcome');
    });
    // Magic Moment for a stand-in device: what a passed connection test hands
    // over, with no control so the screen carries its own final action.
    const unbindMagicMoment = bindOnboardingMagicMomentOpen((target) => {
      const deviceId = toDeviceId(target.deviceId);
      setE2eOpen(true);
      setState('not-onboarded');
      setConnection({
        name: target.name,
        host: target.host,
        video: undefined,
        control: undefined,
        modelId: undefined,
        agentId: undefined,
        addressSource: undefined,
        detected: undefined,
        committedDeviceId: deviceId
      });
      setAddedDeviceId(deviceId);
      setStep('magic-moment');
    });

    // Bug 3 (known-model 1-click close) E2E entry. Opens the funnel on the
    // discovery screen, and drives the SAME onConfigured handoff the known-model
    // 1-click add now fires — so a non-HIL spec can prove the funnel advances out
    // of the discovery screen after the add, without a live camera. The setters
    // and toConnectionTestInput are stable, so the mount-time closures are safe;
    // completeKnownModelAdd inlines handleConfigured's body to avoid a stale
    // render closure. E2E-gated in the probe, so this is inert in production.
    const unbindOneClick = bindOnboardingOneClickControl({
      openAtDiscovery: () => {
        setE2eOpen(true);
        setState('not-onboarded');
        setStep('discovery');
      },
      completeKnownModelAdd: (exit) => {
        setConnection(toConnectionTestInput(exit));
        setStep('connection-test');
      }
    });

    return () => {
      unbindAutoLink();
      unbindOneClick();
      unbindMagicMoment();
    };
  }, []);
  const bridgeSteps = useBridgeSteps();
  const autoLinkInput = {
    autoLinkRunsOnRoute: location.pathname === '/',
    state: autoLinkState
  };
  const autoLinkFailed = isAutoLinkFailedForOnboarding(autoLinkInput);

  // Entry latch: the funnel may OPEN only once Auto Link has settled and the
  // boot overlay is down (or an E2E probe opened it deterministically). Latched,
  // not live — once open the funnel owns its steps, and a later transient
  // controller state must not yank a dialog the user is already in. The two
  // overlay inputs are the ones BootOverlay itself dismisses on. See mayOpenFunnel.
  const [funnelReleased, setFunnelReleased] = useState(false);
  const canOpenFunnel = mayOpenFunnel({
    autoLink: autoLinkInput,
    rootReady: isRootReady,
    registryLoaded: hasRegistryLoaded,
    e2eOpen
  });
  useEffect(() => {
    if (canOpenFunnel) setFunnelReleased(true);
  }, [canOpenFunnel]);
  // Auto Link owns the current room association. A saved room id can paint this
  // row green early, while a newly confirmed Auto Link room can land after this
  // hook instance took its one-time preference snapshot. The pure decision
  // accepts the current run's room without reopening the stale-room shortcut.
  const connectionSteps = {
    ...bridgeSteps,
    roomLinked: isStudioLinkedForOnboarding(autoLinkInput, bridgeSteps.roomLinked)
  };

  // Ready means all three rows are done. The connecting screen exists to be
  // left, so readiness advances the funnel rather than sitting on a completed
  // checklist.
  const bridgeReady =
    connectionSteps.agentDetected &&
    connectionSteps.bridgeAuthenticated &&
    connectionSteps.roomLinked;

  // TWO separate clocks, deliberately.
  //
  // This is the connecting screen's failure ceiling. It starts only when the
  // user is on that screen AND Auto Link is ready. Otherwise Auto Link's
  // setup time consumes this separate budget and the screen can declare Bridge
  // detection failed while Auto Link is still working or has already failed.
  const bridgeFailed = useBridgeTimeout(
    shouldArmBridgeCeiling(step, state, autoLinkInput),
    bridgeReady,
    retryCount
  );

  // The other clock only stops the gate hanging when the activation read failed
  // and the bridge never arrives. It decides whether to SHOW the funnel at all,
  // never whether the bridge failed, so it must not feed the screen above.
  const settleTimedOut = useBridgeTimeout(state === 'unknown', bridgeReady);

  // Time on the connecting screen, for the minimum dwell. An already-healthy
  // Bridge shows the screen with its checks already green and then moves on —
  // the point is that the user SEES the checks happened, not that we animate
  // work that is already done. See MIN_DETECTING_DWELL_MS.
  const onDetecting = step === 'detecting';
  // Clocked from READINESS, not from arrival: the point is that the user sees
  // three green rows for a beat before the screen goes. See
  // MIN_DETECTING_DWELL_MS.
  const readyMs = useElapsedSince(onDetecting && bridgeReady, MIN_DETECTING_DWELL_MS);

  // A studio already at its plan's source limit skips the camera step entirely.
  // See stepAfterDetecting.
  //
  // FALSE unless the limit is both known and reached. An unknown or failed
  // billing read must never send a user with room to spare past the step that
  // adds their camera (ENG-4165/ENG-4187).
  const atSourceLimit = useBillingLimitsState().status === 'loaded' && !limitCanAddCamera();

  useEffect(() => {
    if (onDetecting && mayLeaveDetecting(bridgeReady, readyMs, MIN_DETECTING_DWELL_MS)) {
      setStep(stepAfterDetecting(atSourceLimit));
    }
  }, [onDetecting, bridgeReady, readyMs, atSourceLimit]);

  // Settings -> "Restart onboarding" (ENG-3796 re-entry, original spec section 5).
  //
  // Reopens at the welcome screen and drops every answer the previous pass
  // collected, so a restart is a first launch rather than a resume holding a
  // stale camera, a stale model and a device id the room no longer has.
  //
  // It does not touch the server flag: `onboarded` stays true, which is why the
  // funnel does not come back by itself on the next launch. The request is
  // cleared here rather than by the caller — the store carries an ask, and the
  // gate is the only thing that can answer it, which is also what lets a second
  // click restart again.
  const restartRequested = useOnboardingRestart((µ) => µ.restartRequested);
  const clearRestart = useOnboardingRestart((µ) => µ.clearRestart);

  useEffect(() => {
    if (!restartRequested) return;

    clearRestart();
    // Re-entry from Settings is a different population from a first launch:
    // one is a user being onboarded, the other is a user who chose to see it
    // again, and mixing them makes the completion rate meaningless.
    // A note from the previous link must not put that camera back on screen,
    // including after the camera was removed or a plan upgrade re-enabled this
    // button.
    dropResume();
    sawResumeCameraRef.current = false;
    restartedRef.current = true;
    setState('not-onboarded');
    setStep('welcome');
    setConnection(undefined);
    setAddedDeviceId(undefined);
  }, [restartRequested, clearRestart, dropResume]);

  // Unknown resolves once the bridge is up. Waiting for that is what keeps a
  // returning user from seeing a flash of "Welcome to Iris" on a read that was
  // only ever going to fail early — by the time the bridge is ready, a genuine
  // returning user's activation read has had every chance to succeed.
  useEffect(() => {
    // Either signal settles it. A ready bridge means a returning user's read
    // had every chance to succeed; the ceiling means it is never going to, and
    // a new user must not be denied onboarding because the network was rude.
    if (state === 'unknown' && (bridgeReady || settleTimedOut)) setState('not-onboarded');
  }, [state, bridgeReady, settleTimedOut]);

  useEffect(() => {
    // Nothing to ask about until there is a user to ask about.
    if (!isAuthenticated) return;
    // No flag, no fetch: an environment with the funnel switched off must not
    // even ask the server about onboarding.
    if (!funnelEnabled) return;

    // E2E opens a deterministic state and must not race the account's real answer.
    if (e2eOpen) {
      setState('not-onboarded');
      return;
    }

    let cancelled = false;

    void (async (): Promise<void> => {
      try {
        const token = await getAccessTokenSilently();
        const signals = await fetchActivation(token, config.apiEndpoint);
        // A resumed run owns the answer: the link that interrupted it is
        // exactly what made the server say "onboarded".
        if (!cancelled && !resumedRef.current) {
          setState(signals.onboarded ? 'onboarded' : 'not-onboarded');
        }
      } catch {
        // The activation read failing is EXPECTED in ITG, not exceptional: it
        // 403s until the bridge is up, which is the condition that produced
        // "the wizard never appears" the first time this was built. Staying
        // silent forever would mean a genuinely new user never gets onboarded
        // because of a transient read.
        //
        // So record that the answer is unknown rather than pretending it is
        // still in flight, and let the bridge decide below.
        if (!cancelled) setState('unknown');
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, funnelEnabled, e2eOpen, getAccessTokenSilently, config.apiEndpoint]);

  // Ask again while the answer can still change under us.
  //
  // The single read above is taken the instant the gate mounts. Linking a
  // camera reloads the renderer and the server does not learn about that
  // camera until its config is written seconds to minutes later, so on a
  // fresh mount that read can truthfully answer "not onboarded" about an
  // account that already has a camera — and without this, that answer stands
  // for the life of the page and the welcome screen with it.
  //
  // Only while the user is still sitting on `welcome`, and only to the
  // ceiling. See shouldRecheckOnboarded.
  useEffect(() => {
    if (!isAuthenticated || !funnelEnabled) return;
    // E2E places the gate deterministically; a poll would race the fixture.
    if (e2eOpen) return;
    if (!shouldRecheckOnboarded({ state, step, elapsedMs: 0 })) return;

    let cancelled = false;
    const startedAt = Date.now();

    const interval = setInterval(() => {
      if (!shouldRecheckOnboarded({ state, step, elapsedMs: Date.now() - startedAt })) {
        clearInterval(interval);
        return;
      }

      void (async (): Promise<void> => {
        try {
          const token = await getAccessTokenSilently();
          const signals = await fetchActivation(token, config.apiEndpoint);
          // Only ever promotes. A read that still says "no" leaves the funnel
          // exactly where it is, and a failed read is the expected 403 while
          // the bridge starts — neither may close or reopen anything.
          if (!cancelled && signals.onboarded) setState('onboarded');
        } catch {
          // Same policy as the read above: silence, and try again next tick.
        }
      })();
    }, ONBOARDED_RECHECK_INTERVAL_MS);

    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [
    isAuthenticated,
    funnelEnabled,
    e2eOpen,
    state,
    step,
    getAccessTokenSilently,
    config.apiEndpoint
  ]);

  // Pick the interrupted run back up.
  //
  // Linking an embedded camera reloads the renderer (credentials-dialog.tsx,
  // ENG-3057), which destroys everything the funnel knew about the run: the
  // camera, the step, the handoff from the Add Video Source workflow. The user
  // linked their first camera and then either saw "Welcome to Iris" again — the
  // ENG-4649 report — or, once the link records onboarding, nothing at all.
  // Neither finishes the flow they started.
  //
  // So resume on the magic moment for the camera they just linked, and let the
  // normal completion path run from there. The connection test is deliberately
  // skipped: the camera linked itself, so there is nothing left to prove and
  // re-running three checks against a working camera would be theatre.
  //
  // The screen is already up from the note. This effect only swaps the viewer
  // onto the real device id, or drops the note if that camera is removed.
  useEffect(() => {
    if (!resumePending || resumedRef.current || resumeRecord === undefined) return;

    const camera = cameraToResumeOn(roomCameras, resumeRecord.agentId);
    const settledCount = camera === undefined ? 0 : 1;
    const action = decideResume({
      pending: true,
      settledCount,
      sawSettled: sawResumeCameraRef.current,
      exit: 'tick'
    });
    if (camera !== undefined) sawResumeCameraRef.current = true;
    if (action === 'keep') return;

    if (action === 'drop') {
      dropResume();
      sawResumeCameraRef.current = false;
      setConnection(undefined);
      setAddedDeviceId(undefined);
      setState('onboarded');
      setStep('welcome');
      return;
    }

    if (camera === undefined) return;
    dropResume();

    setConnection({
      name: camera.name,
      // The note's host is the address the user picked in discovery. An
      // embedded camera streams from itself, so its own device record reports
      // 0.0.0.0, and switching to it turned "Camera connected • 192.168.1.221"
      // into "• 0.0.0.0" the moment the camera settled (seen live 2026-10-05).
      host: resumeRecord.host ?? camera.host,
      video: undefined,
      // No control config was ever resolved for an embedded link, so the magic
      // moment carries "Start using Iris" itself rather than handing on to the
      // celebration screen. Same branch a control-less camera already takes.
      control: undefined,
      modelId: undefined,
      agentId: undefined,
      addressSource: undefined,
      detected: undefined,
      committedDeviceId: camera.id as DeviceModel['id']
    });
    setAddedDeviceId(camera.id as DeviceModel['id']);
    setStep('magic-moment');
    // The link already recorded onboarding server-side, so the activation read
    // says "onboarded" and would draw no screen. A run in progress outranks it.
    setState('not-onboarded');
  }, [resumePending, resumeRecord, roomCameras]);

  // The note is already gone once the real camera is on screen. Unlinking that
  // camera has to close the screen itself, or Restart onboarding is staring at
  // a camera that is no longer in the room.
  const showedDeviceRef = useRef(false);
  useEffect(() => {
    if (resumePending) return;
    const present =
      addedDeviceId !== undefined && roomCameras.some((camera) => camera.id === addedDeviceId);
    if (present) showedDeviceRef.current = true;
    if (
      !shouldCloseShownCamera({
        deviceId: addedDeviceId,
        roomIds: roomCameras.map((camera) => camera.id),
        seenInRoom: showedDeviceRef.current
      })
    ) {
      return;
    }
    showedDeviceRef.current = false;
    setConnection(undefined);
    setAddedDeviceId(undefined);
    setState('onboarded');
    setStep('welcome');
  }, [addedDeviceId, resumePending, roomCameras]);

  // The Add Video Source workflow finished its last shared step.
  //
  // Everything the funnel needs is resolved by then — the address the panel
  // proved streams, the chosen model, its control template, the Bridge that
  // will perform the add — so this hands the whole answer to the connection
  // test rather than the funnel reassembling it from screens. The mapping is
  // pure and lives in add-handoff.ts; this only decides WHEN it happens.
  const handleConfigured = (exit: OnboardingExit): void => {
    setConnection(toConnectionTestInput(exit));
    setStep('connection-test');
  };

  // "Just let me explore the app" — an explicit decision to defer, so it is
  // persisted. Optimistic on purpose: the modal closes immediately and the
  // write is not awaited. The endpoint is an idempotent CAS, so a failure here
  // costs a repeat of this screen on the next launch and nothing else — which
  // is a far better outcome than making the user wait on a round-trip to leave
  // a screen they just told us they do not want.
  const handleSkip = (): void => {
    // Start using Iris ends the run. A pending note must not keep the camera
    // screen up after this.
    dropResume();
    // Two different exits, two different events. A user who finished with a
    // working camera and a user who chose to explore the app instead are the
    // two outcomes this funnel exists to tell apart; one event for both would
    // make the completion rate meaningless. Both names are ENG-4305's —
    // `onboarding_succeeded` and `onboarding_skipped` — see
    // hooks/use-onboarding-tracker.ts for why this funnel stopped minting its
    // own.
    const cameraAdded = addedDeviceId !== undefined;
    const exitContext = onboardingContextFor(cameraAdded ? 'complete' : (step as never));

    if (cameraAdded) {
      tracker.trackSucceeded(exitContext, { camera_added: true });
    } else {
      tracker.trackSkipped(exitContext, onboardingAbandonProperties('skip'));
    }
    // Report the terminal outcome before closing can unmount the analytics hook.
    setState('onboarded');

    // A user who added a camera is already activated, and the server derives
    // onboarded from that — so the write is a round-trip that cannot change the
    // answer. Skipping it also keeps ONE writer of this fact for the case that
    // needs it (see shouldMarkOnboarded).
    if (!shouldMarkOnboarded({ cameraAdded: addedDeviceId !== undefined })) return;

    void (async (): Promise<void> => {
      try {
        const token = await getAccessTokenSilently();
        await markOnboarded(token, config.apiEndpoint);
      } catch {
        // Swallowed by design — see above. The next launch retries.
      }
    })();
  };

  // Escape / the X. Closes for THIS launch only, so the funnel returns next
  // time. The terminal event separates an intentional dismissal from a crash
  // or network loss, but unlike handleSkip it never writes the onboarding flag.
  const handleDismiss = (): void => {
    // A dismiss during the post-reload hold must drop the note. Leaving it
    // would put the camera screen back on the next paint.
    dropResume();
    tracker.trackSkipped(
      onboardingContextFor(step as never),
      onboardingAbandonProperties('dismiss')
    );
    setState('onboarded');
    setStep('welcome');
  };

  const handleAutoLinkRetry = (): void => {
    void (async (): Promise<void> => {
      // Reload alone preserves attempts_exhausted, so release only this user's throttle first.
      await getAutoLinkController().clearAttemptsForUser(auth0User?.sub ?? '');
      window.location.reload();
    })();
  };

  // ONE decision, made by the pure function the tests cover. Rendering off
  // inline conditionals is how the funnel previously showed a failure screen
  // while its own progress rows were still turning green — a state no test
  // could see, because the logic only existed inside JSX.
  // ENG-4175 merge: another first-run flow can own the whole screen. See
  // funnelDefersToRoute — this is a per-render deferral, not a switch-off, so
  // the activation read above still runs and the funnel appears the moment that
  // route hands the screen back.
  // ENG-4483 (ITB #002): the boot overlay's zero-Studio setup screen is the
  // signed-off linking progress surface. While it is up, the WHOLE funnel
  // stands down — welcome included, not just the connecting screen — instead
  // of stacking a second setup surface on top of it; same per-render deferral
  // shape as funnelDefersToRoute. QA (ITB 2026-09-11) caught the welcome
  // dialog painting over "Setting up your studio…" on a new account's first
  // login when only `detecting` deferred; see selectFunnelScreen.
  const bootstrapOwnsScreen = bootstrapOwnsLinkingScreen({
    state: autoLinkState,
    isOverlayVisible
  });
  const funnelMayPaint = funnelEnabled && funnelReleased && !funnelDefersToRoute(location.pathname);
  // The resume note is enough. Do not wait for the flag, the boot latch, or
  // the room to report the camera. The funnel is still desktop-only. A pending
  // note also outranks the boot overlay: this reload is mid-link, not a cold
  // boot, and the overlay latch resets on every page load.
  const resumeMayPaint = mayPaintResume({
    pending: resumePending,
    isElectron: isElectronApp(),
    pathname: location.pathname
  });
  const screen = screenWhileResumePending(
    funnelMayPaint
      ? selectFunnelScreen(state, step, bridgeFailed, autoLinkFailed, bootstrapOwnsScreen)
      : null,
    resumeMayPaint
  );

  // Hold the claim exactly as long as a funnel screen is on screen, and drop it
  // the moment one is not — including the deferred-route case above, where the
  // funnel renders nothing and has no business suppressing anything.
  useEffect(() => {
    if (screen === null) {
      releasePermissions();
      return;
    }

    claimPermissions();
    return releasePermissions;
  }, [claimPermissions, releasePermissions, screen]);

  // THE DENOMINATOR. Every other onboarding event is a numerator over the
  // population that saw the funnel, and without this row that population is
  // unknown — no conversion rate in this funnel is computable.
  //
  // Keyed on a ref rather than the screen value so it fires once per launch
  // that OPENS the funnel, not once per screen change, and never for a user the
  // gate waved straight through.
  const enteredRef = useRef(false);
  useEffect(() => {
    if (screen === null || enteredRef.current) return;
    enteredRef.current = true;
    tracker.trackStarted(onboardingContextFor('welcome'), {
      // A first launch and a deliberate re-entry from Settings are different
      // populations. Reported here rather than as a separate event so the
      // denominator stays ONE row per opened funnel.
      entry_source: restartedRef.current ? 'settings_restart' : 'first_run'
    });
  }, [screen, tracker]);

  return (
    <>
      {children}
      {screen === 'welcome' && (
        <FunnelWelcome
          onNext={() => setStep('bridge-concept')}
          onSkip={handleSkip}
          onDismiss={handleDismiss}
        />
      )}
      {screen === 'bridge-concept' && (
        <FunnelBridgeConcept
          // Always via `permissions`. The step itself reads the OS state and
          // forwards to `detecting` immediately when there is nothing to ask,
          // which keeps the permissions hook off the browser render path
          // entirely — the gate mounts on every surface, that hook does not.
          onNext={() => setStep('permissions')}
          onSkip={handleSkip}
          onDismiss={handleDismiss}
        />
      )}
      {screen === 'permissions' && <ScreenPermissions onNext={() => setStep('detecting')} />}
      {screen === 'detecting' && (
        <FunnelDetecting
          attemptNumber={retryCount + 1}
          steps={connectionSteps}
          recovering={retryCount > 0}
          onDismiss={handleDismiss}
        />
      )}
      {screen === 'failure' && (
        // No skip: this screen does not record "I'm done" for a user with no
        // working Bridge. It does take a dismiss, like every other screen.
        <BridgeFailureState
          attemptNumber={retryCount + 1}
          steps={connectionSteps}
          // Straight back to the live checks, not to the intro screen, and
          // bump the retry key so the wait actually starts over. Returning to
          // an already-expired wait made the button read as broken.
          onRetry={() => {
            setRetryCount((µ) => µ + 1);
            setStep('detecting');
          }}
          onDismiss={handleDismiss}
        />
      )}
      {screen === 'auto-link-failure' && <AutoLinkFailureState onRetry={handleAutoLinkRetry} />}
      {/* The camera steps ARE the Add Video Source modal (ENG-3796). Discover,
          Authenticate Source, Configure video and Source details are the
          production panels, unchanged; the funnel only supplies the handoff
          that replaces Connect controls with its own connection test.

          No FunnelShell wraps these: the shell is a native <dialog> in the
          browser's top layer and the modal is a Radix portal, so a shell open
          at the same time would paint over it. See
          components/onboarding-add-source.tsx. */}
      {(screen === 'discovery' || screen === 'manual-entry') && (
        <OnboardingAddSource
          // KEYED ON THE FORK, and it is load-bearing. Both choices render the
          // same component at the same position, so React keeps one instance
          // alive across a switch between them — and the workflow's store is
          // created ONCE per mount, seeding its opening panel from `entry` at
          // that moment. Without this key, going back and picking the other
          // option reused the store from the first attempt, so "Scan my
          // network" opened on the manual video panel with the previous
          // camera's address still in it (reported live 2026-08-28).
          key={screen}
          entry={screen}
          onConfigured={handleConfigured}
          // Closing the modal is a dismissal of the funnel, not of one screen:
          // the workflow's own X and its cancel prompt both land here, and
          // there is nothing behind it to return to.
          onDismiss={handleDismiss}
        />
      )}
      {/* The connection test ADDS the camera — there is no bridge probe RPC —
          so it only renders once the workflow has resolved one. Rendering it
          with no subject would add nothing and test nothing. */}
      {screen === 'connection-test' && connection !== undefined && (
        <ScreenConnectionTest
          agentId={connection.agentId}
          // Always the workflow's own resolved configuration. The panel proved
          // this address streams before Continue was ever enabled, and for an
          // ONVIF camera it is the URI the camera itself reported — which is
          // why no onvifControl is passed any more: there is nothing left to
          // probe. See add-handoff.ts.
          discoveredVideo={connection.video}
          addressSource={connection.addressSource}
          // Display only: the name and the "three quick checks against <host>"
          // line. Every field the add actually uses now arrives resolved above,
          // so the rest of the config is left at its defaults rather than
          // carrying values nothing reads.
          camera={{
            ...DEFAULT_CAMERA_CONFIG,
            name: connection.name,
            host: connection.host
          }}
          control={connection.control}
          modelId={connection.modelId}
          detected={connection.detected}
          committedDeviceId={connection.committedDeviceId}
          onDismiss={handleDismiss}
          // Back to the workflow, at the step that chose the model — the same
          // answer the user would want to change. The screen starts removing
          // the provisional device before navigating, but does not make the
          // user wait for the Bridge save.
          onBack={() => setStep('discovery')}
          onSkip={handleSkip}
          onSuccess={(deviceId) => {
            setAddedDeviceId(deviceId);
            setStep('magic-moment');
          }}
        />
      )}
      {/* Only reachable with a device that passed the connection test: the
          viewer needs a live stream and the pad needs something to drive. */}
      {screen === 'magic-moment' && addedDeviceId !== undefined && connection !== undefined && (
        <ScreenMagicMoment
          config={{ name: connection.name, host: connection.host }}
          // One screen per device: a different device starts its own wait.
          key={addedDeviceId}
          // A control config was only built when the chosen model's template
          // had one, so this is the same answer the connection test used to
          // decide whether to show its control row.
          controllable={connection.control !== undefined}
          deviceId={addedDeviceId}
          // No control means the magic moment WAS the last screen — it carries
          // "Start using Iris" itself. A controllable camera still gets the
          // celebration screen after the user moves it.
          onComplete={() => (connection.control === undefined ? handleSkip() : setStep('complete'))}
        />
      )}
      {screen === 'complete' && (
        <ScreenComplete cameraName={connection?.name ?? ''} onDone={handleSkip} />
      )}
      {screen === 'choose-method' && (
        <ScreenChooseMethod
          onPickDiscovery={() => setStep('discovery')}
          onPickManual={() => setStep('manual-entry')}
          onBack={() => setStep('bridge-concept')}
          onDismiss={handleDismiss}
        />
      )}
    </>
  );
};
